import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/typeof";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["paddingLeft", "paddingRight"],
    _excluded2 = ["children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useMemo } from 'react';
import { withTheme } from 'styled-components';
import { border, boxShadow, merge, style, px } from 'styled-system';
import { wrapWithFocus } from '../focus';
import hoist from '../hoist';
export var em = function em(num) {
  return "".concat(num, "em");
};
export var screenMax = function screenMax(n) {
  return "screen and (max-width:".concat(n, "px)");
};
export var screenMin = function screenMin(n) {
  return "screen and (min-width:".concat(n, "px)");
};
export var createMaxMediaQuery = function createMaxMediaQuery(n) {
  return "@media ".concat(screenMax(n));
};
export var createMinMediaQuery = function createMinMediaQuery(n) {
  return "@media ".concat(screenMin(n));
};

var getDisplayName = function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
};

export var useThemeDefaults = function useThemeDefaults(getDefaultProps, Component) {
  var hoc = withTheme( /*#__PURE__*/React.forwardRef(function (props, ref) {
    return /*#__PURE__*/React.createElement(Component, _extends({}, getDefaultProps(props), props, {
      ref: ref
    }));
  })); // https://github.com/styled-components/styled-components/issues/2531

  hoist(hoc, Component);
  hoc.displayName = "useThemeDefaults(".concat(getDisplayName(Component), ")");
  return hoc;
};
export var mergeAll = function mergeAll() {
  var result = {};

  for (var i = 0; i < arguments.length; i++) {
    result = merge(result, i < 0 || arguments.length <= i ? undefined : arguments[i]);
  }

  return result;
};
export var coerceFromString = function coerceFromString(val) {
  return typeof val === 'string' ? [val] : val;
};
export var mapObj = function mapObj(fn, obj) {
  var out = {};
  var i = 0;

  for (var key in obj) {
    out[key] = fn(obj[key], key, i);
    i++;
  }

  return out;
};
export var pipe = function pipe() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return fns.reduce(function (acc, fn) {
    return function () {
      return fn(acc.apply(void 0, arguments));
    };
  });
};
/**
 * Use sparingly!
 * HOF which will recursively apply "!important" to every css property.
 * Warning: if the value is a number, it will be cast to to a px string
 */

export var important = function important(css) {
  return mapObj(function (val) {
    return _typeof(val) === 'object' ? important(val) : typeof val === 'number' ? "".concat(val, "px !important") : "".concat(val, " !important");
  }, css);
};
/**
 * HOF that will transform a css object by taking its `paddingLeft` and
 * `paddingRight` properties and wrapping them in a `:not(:first-child)`
 * or `:not(:last-child)` block, to ensure that text flows from and up to
 * the very edge of the table margin
 *
 * @example
 * import styled from 'styled-components';
 * import { space } from 'styled-system';
 *
 * const Foo = styled('div')(innerPaddingX(space));
 */

export var innerPaddingX = function innerPaddingX(css) {
  var paddingLeft = css.paddingLeft,
      paddingRight = css.paddingRight,
      rest = _objectWithoutProperties(css, _excluded);

  if (paddingLeft) {
    rest[':not(:first-child)'] = {
      paddingLeft: paddingLeft
    };
  }

  if (paddingRight) {
    rest[':not(:last-child)'] = {
      paddingRight: paddingRight
    };
  }

  return rest;
};
/**
 * Pass a prop from the parent to all children, but only when the child doesn't
 * have that prop set on itself
 */

export var passPropToChildren = function passPropToChildren(prop, Component) {
  return function (_ref) {
    var children = _ref.children,
        props = _objectWithoutProperties(_ref, _excluded2);

    return /*#__PURE__*/React.createElement(Component, props, React.Children.map(children, function (c) {
      return c.props[prop] ? c : /*#__PURE__*/React.cloneElement(c, _objectSpread(_defineProperty({}, prop, props[prop]), c.props));
    }));
  };
};
/**
 * Flatten nested fragments, e.g. in cases where a parent expects a flat array of children
 * rather than an array of fragments
 * @param {*} children
 */

export var flattenChildren = function flattenChildren(children) {
  return React.Children.toArray(children).reduce(function (acc, child) {
    if (child.type === React.Fragment) {
      return acc.concat(flattenChildren(child.props.children));
    }

    acc.push(child);
    return acc;
  }, []);
};
export var truncate = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};
export var visibility = style({
  prop: 'visibility'
});
export var whiteSpace = style({
  prop: 'whiteSpace'
});
export var wordBreak = style({
  prop: 'wordBreak'
});
export var textOverflow = style({
  prop: 'textOverflow'
});
export var textTransform = style({
  prop: 'textTransform'
});
export var gridColumn = style({
  prop: 'gridColumn'
});
export var gridColumnStart = style({
  prop: 'gridColumnStart'
});
export var gridColumnEnd = style({
  prop: 'gridColumnEnd'
});
export var gridRow = style({
  prop: 'gridRow'
});
export var gridRowStart = style({
  prop: 'gridRowStart'
});
export var gridRowEnd = style({
  prop: 'gridRowEnd'
});
export var gridArea = style({
  prop: 'gridArea'
});
export var placeSelf = style({
  prop: 'placeSelf'
});
export var placeItems = style({
  prop: 'placeItems'
});
export var placeContent = style({
  prop: 'placeContent'
});
export var flexFlow = style({
  prop: 'flexFlow'
});
export var flexShrink = style({
  prop: 'flexShrink'
});
export var flexGrow = style({
  prop: 'flexGrow'
});
export var transform = style({
  prop: 'transform'
});
export var transition = style({
  prop: 'transition'
});
export var textDecoration = style({
  prop: 'textDecoration'
});
export var WebkitTextFillColor = style({
  prop: 'WebkitTextFillColor'
});
export var WebkitLineClamp = style({
  prop: 'WebkitLineClamp'
});
export var WebkitBoxOrient = style({
  prop: 'WebkitBoxOrient'
});
export var gap = style({
  prop: 'gap'
});
export var outline = style({
  prop: 'outline'
});
export var borderRadius = style({
  prop: "borderRadius",
  alias: "radius",
  key: "radii",
  // assuming that v will only be a number or a string
  transformValue: function transformValue(v, scale) {
    return Number.isInteger(v) ? px(v) : v.replace(/[^\/\s]+/g, function (t) {
      return scale[t] ? px(scale[t]) : t;
    });
  }
});
export var focusOutline = pipe(function (props) {
  return _objectSpread(_objectSpread({}, props), {}, {
    outline: props.focusOutline
  });
}, outline, wrapWithFocus);
export var focusBoxShadow = pipe(function (props) {
  return _objectSpread(_objectSpread({}, props), {}, {
    boxShadow: props.focusBoxShadow
  });
}, boxShadow, wrapWithFocus);
export var focusBorder = pipe(function (props) {
  return _objectSpread(_objectSpread({}, props), {}, {
    border: props.focusBorder,
    borderColor: props.focusBorderColor
  });
}, border, wrapWithFocus);
export var isFunction = function isFunction(v) {
  return typeof v === 'function';
};
export var isShallowEqual = function isShallowEqual(objA, objB) {
  if (!objA) {
    return objA === objB;
  }

  if (!objB) {
    return objA === objB;
  }

  if (objA === objB) {
    return true;
  }

  for (var i in objA) {
    if (!(i in objB)) {
      return false;
    }
  }

  for (var _i in objB) {
    if (objA[_i] !== objB[_i]) {
      return false;
    }
  }

  return true;
}; // e.g. indexBy(v => v.id, [{ id: 'foo', name: 'bar' }]) -> { foo: { id: 'foo', name: 'bar' } }

export var indexBy = function indexBy(getter) {
  return function (arr) {
    var out = {};
    arr.forEach(function (item) {
      out[getter(item)] = item;
    });
    return out;
  };
};
export var deepEqual = function deepEqual(obj1, obj2) {
  if (obj1 == null && obj2 == null) {
    return true;
  }

  if (obj1 == null || obj2 == null) {
    return false;
  }

  var keys1 = Object.keys(obj1);
  var keys2 = Object.keys(obj2);

  if (!keys1 && !keys2) {
    return true;
  }

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (var i in keys1) {
    var value1 = obj1[keys1[i]];
    var value2 = obj2[keys1[i]];
    var equals = void 0;

    if (_typeof(value1) === 'object' || _typeof(value2) == 'object') {
      equals = deepEqual(value1, value2);
    } else {
      equals = value1 === value2;
    }

    if (!equals) {
      return false;
    }
  }

  return true;
};
/**
 * Assign multiple references to an element. There's a equivalent hook called
 * useCombinedRefs, but this method is useful when hooks cannot be used
 */

export var mergeRefs = function mergeRefs() {
  for (var _len2 = arguments.length, refs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    refs[_key2] = arguments[_key2];
  }

  return function (element) {
    return refs.forEach(function (ref) {
      if (!ref) {
        return;
      }

      if (typeof ref === 'function') {
        return ref(element);
      }

      ref.current = element;
    });
  };
};
export var STORY_BOOK_BASE_URL = 'http://localhost.apple.com:9009';
export var getStoryBookComponentUrl = function getStoryBookComponentUrl(storyId, theme) {
  return "".concat(STORY_BOOK_BASE_URL, "/iframe.html?id=").concat(storyId, "&theme=").concat(theme);
};
export var isSmallViewport = function isSmallViewport(_ref2) {
  var bpName = _ref2.bpName;
  return bpName === 'sm';
};
export var noop = function noop() {}; // Puppeteer specific utility

export var clickOn = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(page, selector) {
    var target, targets, firstTarget;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;
            _context.next = 3;
            return page.waitFor(selector);

          case 3:
            _context.next = 5;
            return page.$(selector);

          case 5:
            target = _context.sent;
            _context.next = 8;
            return target.click();

          case 8:
            _context.next = 20;
            break;

          case 10:
            _context.prev = 10;
            _context.t0 = _context["catch"](0);
            _context.next = 14;
            return page.waitForXPath(selector);

          case 14:
            _context.next = 16;
            return page.$x(selector);

          case 16:
            targets = _context.sent;
            firstTarget = targets[0];
            _context.next = 20;
            return firstTarget.click();

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 10]]);
  }));

  return function clickOn(_x, _x2) {
    return _ref3.apply(this, arguments);
  };
}();
export var typeIn = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(page, selector, value) {
    var target, targets, firstTarget;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 3;
            return page.waitFor(selector);

          case 3:
            _context2.next = 5;
            return page.$(selector);

          case 5:
            target = _context2.sent;
            _context2.next = 8;
            return target.type(value);

          case 8:
            _context2.next = 20;
            break;

          case 10:
            _context2.prev = 10;
            _context2.t0 = _context2["catch"](0);
            _context2.next = 14;
            return page.waitForXPath(selector);

          case 14:
            _context2.next = 16;
            return page.$x(selector);

          case 16:
            targets = _context2.sent;
            firstTarget = targets[0];
            _context2.next = 20;
            return firstTarget.type(value);

          case 20:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[0, 10]]);
  }));

  return function typeIn(_x3, _x4, _x5) {
    return _ref4.apply(this, arguments);
  };
}();
/**
 * Generate an `id` when none specified; useful for generating a unique id for use with ARIA attributes.
 *
 * @param id string
 * @param prefix string
 */

export var useRandomId = function useRandomId(id, prefix) {
  return useMemo(function () {
    return id || "".concat(prefix || 'tbid-').concat(Math.round(Math.random() * 100000));
  }, [id, prefix]);
};
export var isPropDefined = function isPropDefined(prop) {
  return typeof prop !== 'undefined';
};